import { ChartDonut } from '../../Chart'
import { BirdviewAgingChart } from '../BirdviewAgingChart'
import { BirdviewBarsChart } from '../BirdviewBarsChart'
import { BirdviewLineChart } from '../BirdviewLineChart'
import {
  BIRDVIEW_CHART_ACTION_OPTIONS,
  BIRDVIEW_CHART_LABEL_KEY,
  BirdviewChartAction,
  BirdviewChartType,
  BirdviewKpiUnit,
} from '../DashboardBirdview.constants.charts'
import {
  BIRDVIEW_CHART_DEFAULT_PERIOD,
  BIRDVIEW_CHART_PERIOD_OPTIONS,
} from '../DashboardBirdview.constants.controls'
import {
  checkIsBirdviewAvgTimeMetric,
  checkIsBirdviewCountMetric,
  checkIsBirdviewPerUnitsMetric,
  formatBirdviewNumber,
  getBirdviewAgeRangeLabelParams,
  getBirdviewChartBarsLimit,
} from '../DashboardBirdview.utils.charts'
import { checkIsBirdviewNormalState } from '../DashboardBirdview.utils.sections'

import { BirdviewChartCardControls } from './BirdviewChartCardControls'

function getBirdviewChartItems(items, t) {
  return items.map(item => ({
    ...item,
    label: item.labelKey
      ? t(item.labelKey, getBirdviewAgeRangeLabelParams(item))
      : item.label,
  }))
}

function getBirdviewChartValueUnit(metric) {
  return checkIsBirdviewAvgTimeMetric(metric)
    ? BirdviewKpiUnit.Days
    : BirdviewKpiUnit.Count
}

// A rate has no share of the total.
function checkIsBirdviewRatioMetric(metric) {
  return (
    checkIsBirdviewPerUnitsMetric(metric) ||
    checkIsBirdviewAvgTimeMetric(metric)
  )
}

function checkIsBirdviewStackedBars({ isStacked, metric }) {
  return isStacked && checkIsBirdviewCountMetric(metric)
}

function getBirdviewChartControlOptions(options, t) {
  return options.map(option => ({ ...option, label: t(option.labelKey) }))
}

function getBirdviewChartOption(options, value) {
  return options.find(option => option.value === value)
}

function checkIsBirdviewChartPeriodAction(action) {
  return action.value === BirdviewChartAction.Period
}

function checkIsBirdviewLineChart(chart) {
  return chart.type === BirdviewChartType.Line
}

// A card of the default size has loaded with its default limit already.
export function checkIsBirdviewChartLimitChanged({ chart, controls, limit }) {
  return limit !== null && limit !== (controls.limit ?? chart.defaultLimit)
}

export function getBirdviewChartControls({ chart, controls, t }) {
  const periodOptions = chart.isSnapshot
    ? null
    : getBirdviewChartControlOptions(BIRDVIEW_CHART_PERIOD_OPTIONS, t)
  const metricOptions = chart.metricOptions
    ? getBirdviewChartControlOptions(chart.metricOptions, t)
    : null
  const metric = metricOptions
    ? (getBirdviewChartOption(metricOptions, controls.metric) ??
      metricOptions[0])
    : null

  return {
    metric,
    metricOptions,
    period: periodOptions
      ? getBirdviewChartOption(
          periodOptions,
          controls.period ?? BIRDVIEW_CHART_DEFAULT_PERIOD.value
        )
      : null,
    periodOptions,
  }
}

export function getBirdviewChartLimit({ chart, rowSpan }) {
  return chart.defaultLimit
    ? getBirdviewChartBarsLimit({ defaultLimit: chart.defaultLimit, rowSpan })
    : null
}

export function getBirdviewNextChartOptionValue(options, value) {
  const index = options.findIndex(option => option.value === value)

  return options[(index + 1) % options.length].value
}

export function getBirdviewChartActions(chart) {
  return chart.isSnapshot
    ? BIRDVIEW_CHART_ACTION_OPTIONS.filter(
        action => !checkIsBirdviewChartPeriodAction(action)
      )
    : BIRDVIEW_CHART_ACTION_OPTIONS
}

export function checkIsBirdviewChartControlsInChart({ chart, state }) {
  return checkIsBirdviewLineChart(chart) && checkIsBirdviewNormalState(state)
}

// Bars read the metric of the loaded section: a refresh keeps the old one.
export function getBirdviewChartContent({
  chart,
  controlsProps,
  section,
  t,
  onOpen,
}) {
  switch (chart.type) {
    case BirdviewChartType.Aging:
      return (
        <BirdviewAgingChart
          items={getBirdviewChartItems(section.items, t)}
          total={section.total}
        />
      )
    case BirdviewChartType.Bars:
      return (
        <BirdviewBarsChart
          color={chart.color}
          isStacked={checkIsBirdviewStackedBars({
            isStacked: chart.isStacked,
            metric: section.metric,
          })}
          items={getBirdviewChartItems(section.items, t)}
          note={
            chart.noteKey
              ? t(chart.noteKey, {
                  count: formatBirdviewNumber(section.total),
                })
              : null
          }
          total={section.total}
          valueUnit={getBirdviewChartValueUnit(section.metric)}
          withShare={!checkIsBirdviewRatioMetric(section.metric)}
          onOpen={onOpen}
        />
      )
    case BirdviewChartType.Donut:
      return (
        <ChartDonut
          items={getBirdviewChartItems(section.items, t)}
          nameKey={BIRDVIEW_CHART_LABEL_KEY}
          total={section.total}
        />
      )
    case BirdviewChartType.Line:
      return (
        <BirdviewLineChart
          Controls={BirdviewChartCardControls}
          closed={section.closed}
          controlsProps={controlsProps}
          opened={section.opened}
          points={section.items}
        />
      )
    default:
      return null
  }
}
